import { z } from "zod";
/** kg CO₂e per unit of consumption (same semantics as former financial emission fields). */
export var EmissionFactorType;
(function (EmissionFactorType) {
    // Scope 1 — fuel burned on site
    EmissionFactorType["Diesel"] = "emission-factor-type-scope1_diesel";
    EmissionFactorType["Petrol"] = "emission-factor-type-scope1_petrol";
    EmissionFactorType["Lpg"] = "emission-factor-type-scope1_lpg";
    EmissionFactorType["NaturalGas"] = "emission-factor-type-scope1_natural-gas";
    /** B100. Its CO₂ is biogenic, so it is ~31× smaller than fossil diesel at
     * the rows we default from — 0.0865 vs 2.7163 kg CO₂e/L. B5, B10, B20 and
     * B50 are blends of this and `Diesel`, not factors of their own; see
     * `FUEL_BLENDS`. */
    EmissionFactorType["DieselBiodiesel"] = "emission-factor-type-scope1_diesel-biodiesel";
    /** Hydrotreated vegetable oil / renewable diesel — biogenic CO₂ again. */
    EmissionFactorType["RenewableDiesel"] = "emission-factor-type-scope1_renewable-diesel";
    /** The ethanol share of E10. Biogenic CO₂ again: 0.00936 against petrol's
     * 2.3126 kg CO₂e/L. Only ever a blend component — no leaf reports neat
     * ethanol — so it is asked for exactly when a project reports E10. */
    EmissionFactorType["PetrolEthanol"] = "emission-factor-type-scope1_petrol-ethanol";
    /** Petroleum-based oils other than as fuel. */
    EmissionFactorType["Lubricants"] = "emission-factor-type-scope1_lubricants";
    EmissionFactorType["Greases"] = "emission-factor-type-scope1_greases";
    EmissionFactorType["Solvents"] = "emission-factor-type-scope1_solvents";
    // Scope 2
    EmissionFactorType["Electricity"] = "emission-factor-type-scope2_electricity";
    // Scope 3 — the upstream partner of a Scope 1 or Scope 2 factor above.
    // Burning a litre of fuel and producing that litre are two emissions in two
    // scopes from one reported quantity; the standard publishes a well-to-pump
    // factor beside every fuel factor, and a transmission-loss factor beside
    // every electricity factor. `PRIMARY_TO_UPSTREAM` pairs them.
    EmissionFactorType["DieselUpstream"] = "emission-factor-type-scope3_diesel-upstream";
    EmissionFactorType["PetrolUpstream"] = "emission-factor-type-scope3_petrol-upstream";
    EmissionFactorType["LpgUpstream"] = "emission-factor-type-scope3_lpg-upstream";
    EmissionFactorType["NaturalGasUpstream"] = "emission-factor-type-scope3_natural-gas-upstream";
    EmissionFactorType["ElectricityUpstream"] = "emission-factor-type-scope3_electricity-upstream";
    // Scope 3 — embodied and disposal
    EmissionFactorType["MaterialsConcreteReadyMix"] = "emission-factor-type-scope3_materials-concrete-ready-mix";
    EmissionFactorType["MaterialsPrecastConcrete"] = "emission-factor-type-scope3_materials-precast-concrete";
    EmissionFactorType["MaterialsBitumen"] = "emission-factor-type-scope3_materials-bitumen";
    EmissionFactorType["MaterialsWoodTimber"] = "emission-factor-type-scope3_materials-wood-timber";
    EmissionFactorType["MaterialsCement"] = "emission-factor-type-scope3_materials-cement";
    EmissionFactorType["MaterialsSteel"] = "emission-factor-type-scope3_materials-steel";
    EmissionFactorType["MaterialsSteelCoil"] = "emission-factor-type-scope3_materials-steel-coil";
    EmissionFactorType["MaterialsPiping"] = "emission-factor-type-scope3_materials-piping";
    EmissionFactorType["MaterialsPlasticsSheetsFilm"] = "emission-factor-type-scope3_materials-plastics-sheets-film";
    EmissionFactorType["MaterialsComposites"] = "emission-factor-type-scope3_materials-composites";
    EmissionFactorType["MaterialsCoatingsFinishes"] = "emission-factor-type-scope3_materials-coatings-finishes";
    EmissionFactorType["MaterialsCables"] = "emission-factor-type-scope3_materials-cables";
    EmissionFactorType["MaterialsWaterTretmentChemicals"] = "emission-factor-type-scope3_materials-water-tretment-chemicals";
    EmissionFactorType["MaterialsCarpet"] = "emission-factor-type-scope3_materials-carpet";
    EmissionFactorType["MaterialsAsphalt"] = "emission-factor-type-scope3_materials-asphalt";
    EmissionFactorType["MaterialsAluminium"] = "emission-factor-type-scope3_materials-aluminium";
    EmissionFactorType["MaterialsGlass"] = "emission-factor-type-scope3_materials-glass";
    EmissionFactorType["MaterialsAggregates"] = "emission-factor-type-scope3_materials-aggregates";
    EmissionFactorType["TransportRoadRigidTruck"] = "emission-factor-type-scope3_transport-road-rigid-truck";
    EmissionFactorType["TransportRoadArticulatedTruck"] = "emission-factor-type-scope3_transport-road-articulated-truck";
    EmissionFactorType["TransportRail"] = "emission-factor-type-scope3_transport-rail";
    EmissionFactorType["TransportSea"] = "emission-factor-type-scope3_transport-sea";
    EmissionFactorType["TransportOther"] = "emission-factor-type-scope3_transport-other";
    EmissionFactorType["WasteConstructionDemolition"] = "emission-factor-type-scope3_waste-construction-demolition";
    EmissionFactorType["WasteSolidLiquidMixed"] = "emission-factor-type-scope3_waste-solid-liquid-mixed";
    EmissionFactorType["WasteSpoilFillSoilMaterial"] = "emission-factor-type-scope3_waste-spoil-fill-soil-material";
    EmissionFactorType["WasteOffice"] = "emission-factor-type-scope3_waste-office";
})(EmissionFactorType || (EmissionFactorType = {}));
export var EmissionFactorScope;
(function (EmissionFactorScope) {
    EmissionFactorScope[EmissionFactorScope["Scope1"] = 1] = "Scope1";
    EmissionFactorScope[EmissionFactorScope["Scope2"] = 2] = "Scope2";
    EmissionFactorScope[EmissionFactorScope["Scope3"] = 3] = "Scope3";
})(EmissionFactorScope || (EmissionFactorScope = {}));
export const getEmissionFactorScope = (emissionFactorType) => {
    switch (emissionFactorType) {
        case EmissionFactorType.Diesel:
        case EmissionFactorType.Petrol:
        case EmissionFactorType.Lpg:
        case EmissionFactorType.NaturalGas:
        case EmissionFactorType.DieselBiodiesel:
        case EmissionFactorType.RenewableDiesel:
        case EmissionFactorType.PetrolEthanol:
        case EmissionFactorType.Lubricants:
        case EmissionFactorType.Greases:
        case EmissionFactorType.Solvents:
            return EmissionFactorScope.Scope1;
        case EmissionFactorType.Electricity:
            return EmissionFactorScope.Scope2;
        default:
            return EmissionFactorScope.Scope3;
    }
};
/**
 * The one producer of the string "Scope N" in the codebase.
 *
 * Two conventions exist for the same three buckets: this numeric enum, which is
 * what the scope DECISION is made in, and the "Scope N" string, which is what
 * wire payloads and chart series are keyed by. The string is a label derived
 * from the decision — never a second decision. Anything that needs the label
 * comes through here, so a third spelling can't appear.
 */
export const emissionFactorScopeLabel = (scope) => {
    switch (scope) {
        case EmissionFactorScope.Scope1:
            return "Scope 1";
        case EmissionFactorScope.Scope2:
            return "Scope 2";
        case EmissionFactorScope.Scope3:
            return "Scope 3";
    }
};
/** The emission-factor drawer's section heading for a scope. */
export const emissionFactorScopeToString = (scope) => `Emission Factors (${emissionFactorScopeLabel(scope)})`;
export const emissionFactorSchema = z.object({
    created: z.date(),
    emission_factor_id: z.string(),
    emission_factor_type: z.enum(EmissionFactorType),
    project_id: z.string(),
    value: z.number()
});
export const emissionFactorTypeToString = (t) => {
    switch (t) {
        case EmissionFactorType.Diesel:
            return "Diesel";
        case EmissionFactorType.Petrol:
            return "Petrol";
        case EmissionFactorType.Lpg:
            return "LPG";
        case EmissionFactorType.NaturalGas:
            return "Natural Gas";
        case EmissionFactorType.DieselBiodiesel:
            return "Biodiesel (B100)";
        case EmissionFactorType.RenewableDiesel:
            return "Renewable Diesel (HVO)";
        case EmissionFactorType.PetrolEthanol:
            return "Ethanol";
        case EmissionFactorType.Lubricants:
            return "Lubricating Oils";
        case EmissionFactorType.Greases:
            return "Greases";
        case EmissionFactorType.Solvents:
            return "Solvents";
        case EmissionFactorType.Electricity:
            return "Electricity";
        case EmissionFactorType.DieselUpstream:
            return "Diesel – Upstream";
        case EmissionFactorType.PetrolUpstream:
            return "Petrol – Upstream";
        case EmissionFactorType.LpgUpstream:
            return "LPG – Upstream";
        case EmissionFactorType.NaturalGasUpstream:
            return "Natural Gas – Upstream";
        case EmissionFactorType.ElectricityUpstream:
            return "Electricity – Upstream";
        case EmissionFactorType.MaterialsConcreteReadyMix:
            return "Concrete – Ready mix";
        case EmissionFactorType.MaterialsPrecastConcrete:
            return "Concrete - Precast";
        case EmissionFactorType.MaterialsBitumen:
            return "Bitumen";
        case EmissionFactorType.MaterialsWoodTimber:
            return "Wood / Timber";
        case EmissionFactorType.MaterialsCement:
            return "Cement";
        case EmissionFactorType.MaterialsSteel:
            return "Steel";
        case EmissionFactorType.MaterialsSteelCoil:
            return "Steel - Coil";
        case EmissionFactorType.MaterialsPiping:
            return "Piping";
        case EmissionFactorType.MaterialsPlasticsSheetsFilm:
            return "Plastics - Sheets & Film";
        case EmissionFactorType.MaterialsComposites:
            return "Composites";
        case EmissionFactorType.MaterialsCoatingsFinishes:
            return "Coatings & Finishes";
        case EmissionFactorType.MaterialsCables:
            return "Cables";
        case EmissionFactorType.MaterialsWaterTretmentChemicals:
            return "Water Treatment Chemicals";
        case EmissionFactorType.MaterialsCarpet:
            return "Carpet";
        case EmissionFactorType.MaterialsAsphalt:
            return "Asphalt";
        case EmissionFactorType.MaterialsAluminium:
            return "Aluminium";
        case EmissionFactorType.MaterialsGlass:
            return "Glass";
        case EmissionFactorType.MaterialsAggregates:
            return "Aggregates";
        case EmissionFactorType.TransportRoadRigidTruck:
            return "Road – Rigid Truck";
        case EmissionFactorType.TransportRoadArticulatedTruck:
            return "Road – Articulated Truck";
        case EmissionFactorType.TransportRail:
            return "Rail";
        case EmissionFactorType.TransportSea:
            return "Sea";
        case EmissionFactorType.TransportOther:
            return "Road – Other";
        case EmissionFactorType.WasteConstructionDemolition:
            return "Construction/Demolition Waste";
        case EmissionFactorType.WasteSpoilFillSoilMaterial:
            return "Spoil/Fill/Soil Material";
        case EmissionFactorType.WasteSolidLiquidMixed:
            return "Solid & Liquid Mixed Waste";
        case EmissionFactorType.WasteOffice:
            return "Office Waste";
        default:
            return String(t) + "(unknown)";
    }
};
/**
 * What a factor edit does to months that were already reported.
 *
 * A project edits its factors for two different reasons and the product cannot
 * tell them apart on its own:
 *
 *   - `Forward` — the figure has genuinely changed (a new tariff, a supplier's
 *     updated EPD). Months already ACCEPTED keep what they were signed off
 *     with; everything still open is re-costed.
 *   - `Override` — the old figure was simply wrong, and every month that used
 *     it should be corrected. Irreversible: nothing records the previous
 *     values, so the confirmation that offers this is the only safeguard.
 *
 * `Forward` is the default everywhere. `Override` is only ever reached by
 * someone answering a dialog that says what it will do.
 */
export var EmissionFactorEditMode;
(function (EmissionFactorEditMode) {
    EmissionFactorEditMode["Forward"] = "emission-factor-edit-mode_forward";
    EmissionFactorEditMode["Override"] = "emission-factor-edit-mode_override";
})(EmissionFactorEditMode || (EmissionFactorEditMode = {}));
